"use client"

import { useEffect, useRef, useState } from "react"
import mermaid from "mermaid"
import { Maximize2, X, Download, ZoomIn, ZoomOut, NotebookPen, Check } from "lucide-react"
import { postAws } from "../aws"

const MindMap = ({ message, book }) => {
  const mermaidRef = useRef(null)
  const containerRef = useRef(null)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaved, setIsSaved] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  // Parse the mindmap data from the message
  const parseMindMapData = () => {
    try {
      // If message has mindmap property directly
      if (message.mindmap) {
        // Check if it's a string containing mermaid code
        const mermaidMatch = message.mindmap.match(/```mermaid\s*([\s\S]*?)\s*```/)
        if (mermaidMatch && mermaidMatch[1]) {
          return mermaidMatch[1].trim()
        }

        // Check if it's a JSON string
        if (typeof message.mindmap === "string" && message.mindmap.startsWith("{")) {
          try {
            const parsedMindmap = JSON.parse(message.mindmap)
            if (parsedMindmap.mindmap) {
              return parsedMindmap.mindmap
            }
          } catch (e) {
            // Not valid JSON, continue with other checks
          }
        }

        return message.mindmap
      }

      // Handle the nested JSON structure from the server response
      if (typeof message.message === "string") {
        try {
          // Parse the stringified JSON in the message property
          const parsedMessage = JSON.parse(message.message)

          if (parsedMessage.mindmap) {
            // Check if mindmap is a string containing JSON
            if (typeof parsedMessage.mindmap === "string" && parsedMessage.mindmap.startsWith("{")) {
              try {
                const nestedMindmap = JSON.parse(parsedMessage.mindmap)
                if (nestedMindmap.mindmap) {
                  return nestedMindmap.mindmap
                }
              } catch (e) {
                // Not valid JSON, continue with other checks
              }
            }

            // Extract the mermaid content
            const mermaidMatch = parsedMessage.mindmap.match(/```mermaid\s*([\s\S]*?)\s*```/)
            if (mermaidMatch && mermaidMatch[1]) {
              return mermaidMatch[1].trim()
            }
            return parsedMessage.mindmap
          }
        } catch (e) {
          console.error("Error parsing nested JSON:", e)
        }
      }

      return null
    } catch (error) {
      console.error("Error parsing mindmap data:", error)
      return null
    }
  }

  const mindMapDefinition = parseMindMapData()

  // Toggle fullscreen
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen()
      } else if (containerRef.current.mozRequestFullScreen) {
        // Firefox
        containerRef.current.mozRequestFullScreen()
      } else if (containerRef.current.webkitRequestFullscreen) {
        // Chrome, Safari and Opera
        containerRef.current.webkitRequestFullscreen()
      } else if (containerRef.current.msRequestFullscreen) {
        // IE/Edge
        containerRef.current.msRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        // Firefox
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        // Chrome, Safari and Opera
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        // IE/Edge
        document.msExitFullscreen()
      }
    }
    setIsFullScreen(!isFullScreen)
  }

  // Function to download the mindmap as SVG
  const downloadMindMap = () => {
    if (mermaidRef.current) {
      // Get the SVG element
      const svgElement = mermaidRef.current.querySelector("svg")

      if (svgElement) {
        // Clone the SVG to avoid modifying the displayed one
        const svgClone = svgElement.cloneNode(true)

        // Set proper XML namespace for SVG
        svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg")

        // Convert SVG to string
        const svgData = new XMLSerializer().serializeToString(svgClone)

        // Create a Blob from the SVG data
        const blob = new Blob([svgData], { type: "image/svg+xml" })

        // Create a download URL
        const url = URL.createObjectURL(blob)

        // Create a temporary link element
        const link = document.createElement("a")
        link.href = url
        link.download = "mindmap.svg"

        // Append to body, click and remove
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up the URL object
        URL.revokeObjectURL(url)
      }
    }
  }

  // Zoom functions
  const zoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.1, 2))
  }

  const zoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.1, 0.5))
  }

  const handleSaveToNotes = async () => {
    const notesData = {
      classRoomId: localStorage.getItem("classRoomId"),
      noteType: "mindmap",
      noteTitle: `${book.fileTitle} Mind Map`,
      refTitle: book.fileTitle,
      refId: book._id,
      userId: localStorage.getItem("teacherId"),
      noteContent: JSON.stringify(message),
      mediaUrl: "",
    }

    const saveNote = await postAws("https://api-dev.aurait.ai/notes", "POST", notesData)
    if (saveNote.statusCode === 201) {
      setIsSaved(true)
    }
  }

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(
        document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement,
      )
    }

    document.addEventListener("fullscreenchange", handleFullScreenChange)
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange)
    document.addEventListener("mozfullscreenchange", handleFullScreenChange)
    document.addEventListener("MSFullscreenChange", handleFullScreenChange)

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange)
      document.removeEventListener("webkitfullscreenchange", handleFullScreenChange)
      document.removeEventListener("mozfullscreenchange", handleFullScreenChange)
      document.removeEventListener("MSFullscreenChange", handleFullScreenChange)
    }
  }, [])

  // Add a useEffect to handle initial zoom level based on screen size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768
      if (isMobile) {
        // Start with a larger zoom on mobile for better visibility
        setZoomLevel(isFullScreen ? 1 : 0.8)
      } else {
        setZoomLevel(1)
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [isFullScreen])

  useEffect(() => {
    // Initialize mermaid with enhanced styling
    mermaid.initialize({
      startOnLoad: true,
      theme: "default",
      securityLevel: "loose",
      mindmap: {
        padding: 20,
        useMaxWidth: true,
        htmlLabels: true,
      },
      logLevel: "error",
    })

    // Render the diagram
    if (mermaidRef.current && mindMapDefinition) {
      setIsLoading(true)
      const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`

      // Debug the mindmap definition
      console.log("Mindmap definition:", mindMapDefinition)

      try {
        mermaid
          .render(id, mindMapDefinition)
          .then(({ svg }) => {
            if (mermaidRef.current) {
              mermaidRef.current.innerHTML = svg

              // Apply custom styling to the SVG elements
              const svgElement = mermaidRef.current.querySelector("svg")
              if (svgElement) {
                // Make the SVG responsive
                svgElement.setAttribute("width", "100%")
                svgElement.setAttribute("height", "100%")
                svgElement.style.maxWidth = "none" // Remove any max-width constraints

                // Add viewBox if not present to ensure proper scaling
                if (!svgElement.getAttribute("viewBox")) {
                  const bbox = svgElement.getBBox()
                  // Add padding to the viewBox for better visibility
                  svgElement.setAttribute(
                    "viewBox",
                    `${bbox.x - 20} ${bbox.y - 20} ${bbox.width + 40} ${bbox.height + 40}`,
                  )
                }

                // Ensure the SVG preserves aspect ratio and fits width
                svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet")

                // Increase base font size for better readability
                const allTexts = svgElement.querySelectorAll("text")
                allTexts.forEach((text) => {
                  text.style.fontFamily = "'Inter', 'Segoe UI', Roboto, sans-serif"
                  text.style.fontWeight = "500"
                  // Set minimum font size for better readability
                  const currentFontSize = Number.parseFloat(text.getAttribute("font-size") || "12")
                  if (currentFontSize < 14) {
                    text.setAttribute("font-size", "14")
                  }
                })

                // Add drop shadows to nodes
                const nodes = svgElement.querySelectorAll(".node")
                nodes.forEach((node, index) => {
                  node.style.filter = "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))"

                  // Add colors to main branches
                  if (node.classList.contains("mindmap-node--depth-1")) {
                    const colors = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"]
                    const color = colors[index % colors.length]
                    node.querySelector("rect").setAttribute("fill", color)
                    node.querySelector("text").setAttribute("fill", "white")
                  }
                })

                // Enhance text readability
                const texts = svgElement.querySelectorAll("text")
                texts.forEach((text) => {
                  text.style.fontFamily = "'Inter', 'Segoe UI', Roboto, sans-serif"
                  text.style.fontWeight = "500"

                  // Ensure text is readable on mobile
                  const currentFontSize = Number.parseFloat(text.getAttribute("font-size") || "12")
                  if (window.innerWidth < 768 && currentFontSize < 12) {
                    text.setAttribute("font-size", "12")
                  }
                })

                // Add hover effects via a style element
                const styleElement = document.createElementNS("http://www.w3.org/2000/svg", "style")
                styleElement.textContent = `
                  .node:hover { 
                    transform: scale(1.02); 
                    transition: transform 0.2s ease-in-out;
                    cursor: pointer;
                  }
                  .edgePath:hover {
                    stroke-width: 2.5px;
                    transition: stroke-width 0.2s ease-in-out;
                  }
                `
                svgElement.appendChild(styleElement)
              }

              setIsLoading(false)
            }
          })
          .catch((error) => {
            console.error("Mermaid rendering promise error:", error)
            handleRenderingError(error)
          })
      } catch (error) {
        console.error("Mermaid rendering failed:", error)
        handleRenderingError(error)
      }
    }

    function handleRenderingError(error) {
      if (mermaidRef.current) {
        mermaidRef.current.innerHTML = `
          <div class="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
            <p class="font-medium">Error rendering mind map</p>
            <p class="text-sm mt-1">${error.message}</p>
            <pre class="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto max-h-40">${mindMapDefinition}</pre>
          </div>
        `
      }
      setIsLoading(false)
    }
  }, [mindMapDefinition])

  if (!mindMapDefinition) {
    return (
      <div className="p-5 bg-red-50 text-red-700 rounded-lg border border-red-200 shadow-sm">
        <p className="font-medium">No valid mind map data found</p>
        <p className="text-sm mt-1">Please check the format of your mind map data and try again.</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`relative bg-gradient-to-br from-white to-indigo-50 rounded-xl shadow-lg transition-all duration-300 ${
        isFullScreen ? "fixed inset-0 z-[9999] w-screen h-screen" : "w-full mx-auto"
      }`}
      style={{
        boxShadow: isFullScreen
          ? "none"
          : "0 10px 25px -5px rgba(79, 70, 229, 0.1), 0 8px 10px -6px rgba(79, 70, 229, 0.05)",
      }}
    >
      <div className="flex justify-between items-center mb-3 sm:mb-4">
        <h3 className="text-lg sm:text-xl font-semibold text-indigo-700 flex items-center">
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
              fill="currentColor"
            />
            <path
              d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z"
              fill="currentColor"
            />
          </svg>
          Mind Map
        </h3>
        <div className="flex items-center gap-1 sm:gap-2">
          {book && (
            <div className="relative flex items-center gap-3 mr-2">


              {isHovered && !isSaved && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md whitespace-nowrap shadow-lg">
                  Save to Notes
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
          )}
          {!isFullScreen && (
            <>
              <button
                onClick={zoomOut}
                className="p-1.5 rounded-full hover:bg-white hover:shadow-sm transition-all duration-200"
                aria-label="Zoom out"
                title="Zoom out"
              >
                <ZoomOut className="h-5 w-5 text-indigo-600" />
              </button>
              <button
                onClick={zoomIn}
                className="p-1.5 rounded-full hover:bg-white hover:shadow-sm transition-all duration-200"
                aria-label="Zoom in"
                title="Zoom in"
              >
                <ZoomIn className="h-5 w-5 text-indigo-600" />
              </button>
            </>
          )}
          <button
            onClick={downloadMindMap}
            className="p-1.5 rounded-full hover:bg-white hover:shadow-sm transition-all duration-200"
            aria-label="Download mind map"
            title="Download as SVG"
          >
            <Download className="h-5 w-5 text-indigo-600" />
          </button>
          <button
            onClick={toggleFullScreen}
            className="p-1.5 rounded-full hover:bg-white hover:shadow-sm transition-all duration-200 mr-8"
            aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
            title={isFullScreen ? "Exit full screen" : "Enter full screen"}
          >
            {isFullScreen ? (
              <X className="h-5 w-5 text-indigo-600" />
            ) : (
              <Maximize2 className="h-5 w-5 text-indigo-600" />
            )}
          </button>
        </div>
      </div>

      <div
        className={`relative border border-indigo-100 rounded-lg bg-white overflow-hidden ${
          isFullScreen ? "h-[calc(100%-60px)]" : ""
        }`}
        style={{
          boxShadow: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
              <p className="mt-3 text-indigo-600 font-medium">Rendering mind map...</p>
            </div>
          </div>
        )}

        <div
          ref={mermaidRef}
          className={`mermaid-container overflow-auto p-2 sm:p-4 ${
            isFullScreen ? "h-[calc(100vh-120px)]" : "min-h-[300px]"
          }`}
          style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: "center center",
            transition: "transform 0.2s ease-out",
            width: "100%",
            height: isFullScreen ? "calc(100vh - 120px)" : "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {mindMapDefinition}
        </div>
      </div>


    </div>
  )
}

export default MindMap
